import { useEffect, useMemo, useState } from "react";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
    ResponsiveContainer, CartesianGrid, ComposedChart,
} from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BarChart3, Gauge, TrendingUp } from "lucide-react";

// ---- Conversion constants — keep parallel with FuelEconomyView so the two
//      charts read consistently regardless of which fuel/speed unit the
//      captain has picked. Canonical numerator units in this view:
//        - volume: whatever the captain logged (gal or L) — we just label it
//        - distance: nautical miles (from Cruising Log)
//        - hours: underway hours (from Cruising Log)
//        - cost: vessel display currency (whatever the user has set per row;
//                we sum without FX since most boats are single-currency)
const L_PER_GAL = 3.78541;
const KM_PER_NM = 1.852;
const MI_PER_NM = 1.15078;

// What the user wants to *see* on the fuel chart. The backend payload is unit-
// agnostic; we derive each of these on the fly so flipping the toggle never
// needs another network call.
const FUEL_METRICS = [
    { value: "mpg",   label: "MPG",     hint: "Season avg to date — statute miles per gallon", axis: "MPG" },
    { value: "kml",   label: "km/L",    hint: "Season avg to date — kilometres per litre",     axis: "km/L" },
    { value: "gph",   label: "GPH",     hint: "Season avg to date — burn rate (gph)",           axis: "GPH" },
    { value: "kmh",   label: "km/h",    hint: "Average speed that month (km/h)",                axis: "km/h" },
    { value: "cost_hr",  label: "$/hr",    hint: "Season avg to date — cost per underway hour",  axis: "$/hr" },
    { value: "cost_nm",  label: "$/nm",    hint: "Season avg to date — cost per nautical mile",  axis: "$/nm" },
];

// "Months are gappy" — if a captain didn't fuel up between March and June,
// recharts will draw a single huge connecting line. We fill in gaps with
// nulls so the line breaks naturally. Months returned by the backend are
// already sorted YYYY-MM strings.
function _fillMonthGaps(points) {
    if (!points || points.length === 0) return [];
    const out = [];
    const parse = (m) => { const [y, mm] = m.split("-").map(Number); return new Date(Date.UTC(y, mm - 1, 1)); };
    const fmt = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    let cursor = parse(points[0].month);
    let i = 0;
    const last = parse(points[points.length - 1].month);
    while (cursor.getTime() <= last.getTime()) {
        const key = fmt(cursor);
        if (points[i] && points[i].month === key) {
            out.push(points[i]);
            i++;
        } else {
            out.push({ month: key });
        }
        cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
    }
    return out;
}

// Human-friendly month label for axis ticks. Keep short ("Jun '26") so 12+
// months don't overlap on mobile.
function _fmtMonth(m) {
    if (!m) return "";
    const [y, mm] = m.split("-");
    const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${names[Number(mm) - 1]} '${y.slice(2)}`;
}

// Compute the chosen Y value for a fuel-cost row given the unit mode.
// Returns null when the row doesn't have enough data to compute it — this
// causes recharts to skip the point and break the line naturally.
//
// BUGFIX (Jul 2026): efficiency ratios (MPG/km/L/GPH/$-per) now use
// SEASON-TO-DATE CUMULATIVE totals (row.cum, built in the decorate step)
// instead of single-month buckets. Captains fuel up in one month and burn
// it over the next — with per-month buckets a month almost never had BOTH
// gallons and miles, so the trend lines silently returned null and never
// rendered. Cumulative ratios converge on the true season average and draw
// a point for every month once the first fill + first passage exist.
// km/h (avg speed) stays per-month: distance and hours both come from the
// cruising log, so they're always aligned.
function _yForFuelRow(row, mode) {
    const cum = row.cum || { cost: 0, volGal: 0, nm: 0, hrs: 0 };
    const nmMonth = Number(row.total_distance_nm || 0);
    const hrsMonth = Number(row.total_hours_underway || 0);

    switch (mode) {
        case "mpg": {
            if (cum.volGal <= 0 || cum.nm <= 0) return null;
            return +((cum.nm * MI_PER_NM) / cum.volGal).toFixed(2);
        }
        case "kml": {
            if (cum.volGal <= 0 || cum.nm <= 0) return null;
            return +((cum.nm * KM_PER_NM) / (cum.volGal * L_PER_GAL)).toFixed(2);
        }
        case "gph": {
            if (cum.hrs <= 0 || cum.volGal <= 0) return null;
            return +(cum.volGal / cum.hrs).toFixed(2);
        }
        case "kmh": {
            if (hrsMonth <= 0 || nmMonth <= 0) return null;
            return +((nmMonth * KM_PER_NM) / hrsMonth).toFixed(2);
        }
        case "cost_hr": {
            if (cum.hrs <= 0 || cum.cost <= 0) return null;
            return +(cum.cost / cum.hrs).toFixed(2);
        }
        case "cost_nm": {
            if (cum.nm <= 0 || cum.cost <= 0) return null;
            return +(cum.cost / cum.nm).toFixed(2);
        }
        default:
            return null;
    }
}

// Headline "what's it cost me this year?" — sums every month's total cost
// so captains can see YTD spend at a glance.
function _sumCost(points) {
    return points.reduce((acc, p) => acc + Number(p.total_cost || 0), 0);
}


// =============================================================================
// EngineHoursChart — per-engine cumulative hours over time. Each engine_id
// becomes one series. Recharts handles the responsive layout on mobile.
// =============================================================================
const ENGINE_COLORS = [
    "#d4b572", // gold
    "#5b9fd6", // blue
    "#a78bfa", // violet
    "#f97316", // orange
    "#22d3ee", // cyan
    "#ef4444", // red
];

export function EngineHoursChart({ vesselId }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/vessels/${vesselId}/charts/engine-hours`);
                if (!cancelled) setData(data || []);
            } catch (e) {
                if (!cancelled) setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load engine hours.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [vesselId]);

    // Pivot points → recharts row-per-month with one key per engine_label.
    // Empty months get null Y values so the line breaks instead of bridging
    // months without data. For multi-engine vessels we relabel null engine_id
    // entries as "Unassigned" so they're not confused with the primary engine.
    const { chartRows, engineKeys } = useMemo(() => {
        if (!data.length) return { chartRows: [], engineKeys: [] };
        const distinctEngineIds = new Set(data.map((p) => p.engine_id).filter(Boolean));
        const isMultiEngine = distinctEngineIds.size > 1;
        const labelByEngine = new Map();
        const monthMap = new Map();
        for (const p of data) {
            let label = p.engine_label;
            if (!label) {
                label = isMultiEngine ? "Unassigned" : "Main";
            }
            labelByEngine.set(label, true);
            if (!monthMap.has(p.month)) monthMap.set(p.month, { month: p.month });
            monthMap.get(p.month)[label] = p.hours;
        }
        const months = Array.from(monthMap.keys()).sort();
        const filled = _fillMonthGaps(months.map((m) => monthMap.get(m)));
        return { chartRows: filled, engineKeys: Array.from(labelByEngine.keys()) };
    }, [data]);

    return (
        <div data-testid="engine-hours-chart" className="bm-card" style={{ padding: 22, marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                <Gauge size={18} color="var(--bm-gold)" />
                <h3 className="bm-serif" style={{ fontSize: 18, margin: 0, fontWeight: 500 }}>
                    Engine Hours · history
                </h3>
                <span style={{ color: "var(--bm-text-muted)", fontSize: 13, marginLeft: 8 }}>
                    {engineKeys.length > 1 ? "Per-engine readings, end of month." : "Cumulative hours, end of month."}
                </span>
            </div>

            {err && <div data-testid="engine-hours-err" style={{ color: "var(--bm-bad)", fontSize: 13, marginBottom: 8 }}>{err}</div>}

            {loading && <div style={{ color: "var(--bm-text-muted)", padding: 22, textAlign: "center" }}>Loading…</div>}

            {!loading && chartRows.length === 0 && (
                <div data-testid="engine-hours-empty" style={{ color: "var(--bm-text-muted)", padding: 22, textAlign: "center", fontSize: 13 }}>
                    No engine-hour readings yet. Add maintenance or service entries with the engine-hours field to build this chart.
                </div>
            )}

            {!loading && chartRows.length > 0 && (
                <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={chartRows} margin={{ top: 8, right: 14, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="rgba(28,63,99,0.35)" strokeDasharray="3 3" />
                        <XAxis dataKey="month" tickFormatter={_fmtMonth} stroke="rgba(255,255,255,0.6)" fontSize={12} />
                        <YAxis stroke="rgba(255,255,255,0.6)" fontSize={12} />
                        <Tooltip
                            contentStyle={{ background: "rgba(8,22,40,0.95)", border: "1px solid rgba(212,181,114,0.4)", borderRadius: 8, color: "#fff" }}
                            labelFormatter={_fmtMonth}
                            formatter={(value) => [`${value} hrs`, ""]}
                        />
                        {engineKeys.length > 1 && (
                            <Legend wrapperStyle={{ fontSize: 12, color: "var(--bm-text-muted)" }} />
                        )}
                        {engineKeys.map((label, i) => (
                            <Line
                                key={label}
                                type="monotone"
                                dataKey={label}
                                stroke={ENGINE_COLORS[i % ENGINE_COLORS.length]}
                                strokeWidth={2.5}
                                dot={{ r: 3.5 }}
                                activeDot={{ r: 6 }}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
}


// =============================================================================
// FuelCostChart — composed chart with cost bars + selected-metric line.
// Toggle between MPG / km/L / GPH / km/h / $/hr / $/nm.
// =============================================================================
export function FuelCostChart({ vesselId, displayCurrency = "USD" }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [metric, setMetric] = useState("mpg");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/vessels/${vesselId}/charts/fuel-cost`);
                if (!cancelled) setData(data || []);
            } catch (e) {
                if (!cancelled) setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load fuel cost.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
    }, [vesselId]);

    // Decorate raw points with the user-selected derived metric. We keep the
    // raw cost so the bar chart axis stays stable when the user flips between
    // economy/consumption/cost-rate modes. Season-to-date cumulative totals
    // (normalised to US gallons) are attached first — see _yForFuelRow for why.
    const chartRows = useMemo(() => {
        if (!data.length) return [];
        const sorted = [...data].sort((a, b) => String(a.month).localeCompare(String(b.month)));
        let cost = 0, volGal = 0, nm = 0, hrs = 0;
        const decorated = sorted.map((row) => {
            const vol = Number(row.total_volume || 0);
            volGal += row.fuel_unit === "L" ? vol / L_PER_GAL : vol;
            cost += Number(row.total_cost || 0);
            nm += Number(row.total_distance_nm || 0);
            hrs += Number(row.total_hours_underway || 0);
            const withCum = { ...row, cum: { cost, volGal, nm, hrs } };
            return {
                month: row.month,
                cost: Number(row.total_cost || 0),
                metric: _yForFuelRow(withCum, metric),
            };
        });
        return _fillMonthGaps(decorated);
    }, [data, metric]);

    const metricMeta = FUEL_METRICS.find((m) => m.value === metric) || FUEL_METRICS[0];
    const ytdCost = useMemo(() => _sumCost(data), [data]);
    const fmtCurrency = (n) => {
        try {
            return new Intl.NumberFormat(undefined, { style: "currency", currency: displayCurrency, maximumFractionDigits: 0 }).format(n);
        } catch {
            return `$${Math.round(n).toLocaleString()}`;
        }
    };

    return (
        <div data-testid="fuel-cost-chart" className="bm-card" style={{ padding: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 14 }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <TrendingUp size={18} color="var(--bm-gold)" />
                        <h3 className="bm-serif" style={{ fontSize: 18, margin: 0, fontWeight: 500 }}>
                            Fuel cost · {metricMeta.label}
                        </h3>
                    </div>
                    <p style={{ color: "var(--bm-text-muted)", fontSize: 13, margin: "4px 0 0" }}>
                        Bars = cost per month. Line = {metricMeta.hint.toLowerCase()}.
                        {ytdCost > 0 && (
                            <>{" "}<span style={{ color: "var(--bm-gold)" }}>Total spent: <strong>{fmtCurrency(ytdCost)}</strong></span></>
                        )}
                    </p>
                </div>
                <div data-testid="fuel-cost-metric-toggle" style={{ display: "flex", border: "1px solid var(--bm-border-soft)", borderRadius: "999px", overflow: "hidden", background: "rgba(8,22,40,0.5)", flexWrap: "wrap" }}>
                    {FUEL_METRICS.map((m) => (
                        <button
                            key={m.value}
                            type="button"
                            data-testid={`fuel-cost-metric-${m.value}`}
                            onClick={() => setMetric(m.value)}
                            title={m.hint}
                            style={{
                                padding: "5px 10px",
                                fontSize: 12,
                                border: "none",
                                background: metric === m.value ? "rgba(212,181,114,0.20)" : "transparent",
                                color: metric === m.value ? "var(--bm-gold)" : "var(--bm-text-muted)",
                                cursor: "pointer",
                                fontWeight: metric === m.value ? 600 : 400,
                            }}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            </div>

            {err && <div data-testid="fuel-cost-err" style={{ color: "var(--bm-bad)", fontSize: 13, marginBottom: 8 }}>{err}</div>}

            {loading && <div style={{ color: "var(--bm-text-muted)", padding: 22, textAlign: "center" }}>Loading…</div>}

            {!loading && chartRows.length === 0 && (
                <div data-testid="fuel-cost-empty" style={{ color: "var(--bm-text-muted)", padding: 22, textAlign: "center", fontSize: 13 }}>
                    No Fuel entries yet. Log a fill-up under <strong>Fuel</strong> to build this chart.
                </div>
            )}

            {!loading && chartRows.length > 0 && (
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={chartRows} margin={{ top: 8, right: 14, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke="rgba(28,63,99,0.35)" strokeDasharray="3 3" />
                        <XAxis dataKey="month" tickFormatter={_fmtMonth} stroke="rgba(255,255,255,0.6)" fontSize={12} />
                        <YAxis
                            yAxisId="cost"
                            stroke="#5b9fd6"
                            fontSize={12}
                            tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v}
                            label={{ value: `Cost (${displayCurrency})`, angle: -90, position: "insideLeft", fill: "#5b9fd6", fontSize: 11, dy: 30 }}
                        />
                        <YAxis
                            yAxisId="metric"
                            orientation="right"
                            stroke="#d4b572"
                            fontSize={12}
                            label={{ value: metricMeta.axis, angle: 90, position: "insideRight", fill: "#d4b572", fontSize: 11, dy: -20 }}
                        />
                        <Tooltip
                            contentStyle={{ background: "rgba(8,22,40,0.95)", border: "1px solid rgba(212,181,114,0.4)", borderRadius: 8, color: "#fff" }}
                            labelFormatter={_fmtMonth}
                            formatter={(value, key) => {
                                if (key === "cost") return [fmtCurrency(value), "Cost"];
                                if (value == null) return ["—", metricMeta.label];
                                return [`${value} ${metricMeta.axis}`, metricMeta.label];
                            }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12, color: "var(--bm-text-muted)" }} />
                        <Bar
                            yAxisId="cost"
                            dataKey="cost"
                            fill="#5b9fd6"
                            name={`Cost (${displayCurrency})`}
                            radius={[4, 4, 0, 0]}
                            isAnimationActive={false}
                        />
                        <Line
                            yAxisId="metric"
                            type="monotone"
                            dataKey="metric"
                            stroke="#d4b572"
                            strokeWidth={2.5}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                            name={metricMeta.label}
                            connectNulls={false}
                            isAnimationActive={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            )}
        </div>
    );
}


// Group wrapper used by FuelEconomyView. Both charts side-by-side on desktop,
// stacked on mobile (recharts ResponsiveContainer handles its own width, the
// outer flex column drives the layout).
export default function VesselCharts({ vessel }) {
    return (
        <div data-testid="vessel-charts" style={{ display: "flex", flexDirection: "column", gap: 18, marginTop: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                <BarChart3 size={20} color="var(--bm-gold)" />
                <h2 className="bm-serif" style={{ fontSize: 24, margin: 0, fontWeight: 500 }}>
                    Charts
                </h2>
                <span style={{ color: "var(--bm-text-muted)", fontSize: 13 }}>
                    Monthly trend of your engine hours and fuel cost.
                </span>
            </div>
            <EngineHoursChart vesselId={vessel.id} />
            <FuelCostChart vesselId={vessel.id} displayCurrency={vessel.display_currency || "USD"} />
        </div>
    );
}
